import dayjs from 'dayjs';
import { Space } from 'antd';
import { debounce } from 'lodash';
import {
    FC,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '../../components/PageHeader';
import useBookings from '../../hooks/useBookings';
import { Filters } from '../../components/Bookings';
import TableBookings, { TableBookingsProps } from '../../components/Bookings/Table';
import { TableActions } from '../../components/TableActions';
import { useWorkspaceStore } from '../../store/workspace.store';
import { IBooking } from '../../interfaces/booking.interface';
import { exportTableData } from '../../utils/export.util';

const BOOKING_COLUMNS = ['timeElapsed', 'bookingNumericId', 'createdAt', 'statusText', 'businessName', 'driverName', 'paymentMethod'];

const HistoryBookings: FC = () => {
    const { t } = useTranslation('bookings', { keyPrefix: 'BOOKINGS' });
    const { t: tb } = useTranslation('bookings', { keyPrefix: 'TABLE' });
    const { t: ts } = useTranslation('bookings', { keyPrefix: 'STATUS' });

    const workspace = useWorkspaceStore((state) => state.data);

    const LIMIT = 10;

    const [search, setSearch] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [numericId, setNumericId] = useState<string>('');
    const [columnsSelected, setColumnsSelected] = useState<string[]>(BOOKING_COLUMNS);

    const [loading, bookings, total, refreshData, fetchBookings, paginator, props, refreshDate]: any = useBookings({
        typeBookings: 'onlyPast',
        search,
        args: { limit: LIMIT },
        startDate,
        endDate,
    });

    const fetchDebounced = useCallback<any>(debounce(refreshData, 1000), []);
    const fetchDateDebounced = useCallback<any>(debounce(refreshDate, 1000), []);

    const handleSearch = (e: any) => {
        const { value } = e.target;
        setSearch(value);
        fetchDebounced(value, false);
    };

    const handleNumericId = (e: any) => {
        const { value } = e.target;
        setNumericId(value);
        fetchDebounced(value, true);
    };

    const handleDate = (date: string[]) => {
        const [start, end] = date;
        setStartDate(start);
        setEndDate(end);
        fetchDateDebounced([start, end]);
    };

    useEffect(() => {
        if (!workspace?._id) return;
        fetchBookings();
    }, [workspace?._id, paginator]);

    const columnLabels: Record<string, string> = useMemo(() => ({
        timeElapsed: tb('TIME'),
        bookingNumericId: tb('SERVICE_ID'),
        createdAt: tb('DATE'),
        statusText: tb('STATUS'),
        businessName: tb('STORE'),
        driverName: tb('DRIVER'),
        paymentMethod: tb('PAYMENT_METHOD'),
    }), [tb]);

    const handleExport = () => {
        exportTableData<IBooking>(
            bookings,
            [
                { key: 'bookingNumericId', label: tb('SERVICE_ID') },
                { key: 'createdAt', label: tb('DATE'), render: (v) => dayjs(v).format('DD/MM/YYYY') },
                { key: 'createdAt', label: tb('REQUEST_TIME'), render: (v) => dayjs(v).format('HH:mm') },
                { key: 'statusText', label: tb('STATUS'), render: (v) => ts(v) },
                {
                    key: 'statusText',
                    label: tb('CANCELLED_BY'),
                    render: (v) => {
                        if (!v?.toString().includes('CANCELLED')) return '';
                        return v?.toString().replace('CANCELLED_BY_', '') || '';
                    },
                },
                { key: 'customerName', label: tb('CUSTOMER'), render: (v) => v || '' },
                { key: 'businessName', label: tb('STORE') },
                { key: 'driverName', label: tb('DRIVER'), render: (v) => v || tb('NOT_ASSIGNED') },
                {
                    key: 'carName',
                    label: tb('VEHICLE_TYPE'),
                    render: (v) => {
                        const map: Record<string, string> = { BIKE: 'Motocicleta', LITE: 'Carro' };
                        return map[v as string] || v || '';
                    },
                },
                { key: 'deliveryAddress', label: tb('DELIVERY_ADDRESS'), render: (v) => v || '' },
                { key: 'billedDistance', label: tb('DISTANCE_KM'), render: (v) => (v ? Math.ceil(Number(v)).toString() : '0') },
                { key: 'elapsedTime', label: tb('ELAPSED_TIME'), render: (v) => (v ? Math.round(Number(v) / 60).toString() : '') },
            ],
            `envios-historial-${dayjs().format('YYYY-MM-DD')}`,
        );
    };

    return (
        <div className="screen">
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <PageHeader
                    title={t('PAST_BOOKINGS')}
                    style={{ padding: 0 }}
                />
                <Filters
                    loading={loading}
                    search={search}
                    handleSearch={handleSearch}
                    numericId={numericId}
                    handleNumericId={handleNumericId}
                    startDate={startDate}
                    endDate={endDate}
                    handleDate={handleDate}
                    refreshData={refreshData}
                    extra={(
                        <TableActions
                            columnsSelected={columnsSelected}
                            availableColumns={BOOKING_COLUMNS}
                            columnLabels={columnLabels}
                            onColumnsChange={setColumnsSelected}
                            onExport={handleExport}
                            onReload={() => refreshData()}
                            exportDisabled={!bookings?.length}
                        />
                    )}
                />
                <TableBookings
                    columnsSelected={columnsSelected}
                    loading={loading}
                    total={total}
                    data={bookings}
                    paginator={paginator}
                    refreshData={refreshData}
                    showSort
                    {...props as Partial<TableBookingsProps>}
                />
            </Space>
        </div>
    );
};

export { HistoryBookings };
